import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Printer } from 'lucide-react';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import leiloesService from '../../services/leiloesService';
import { ordemEntradaService } from '../../services/ordemEntradaService';
import { lotesService } from '../../services/lotesService';
import CatalogoBase from '../../components/catalogos/CatalogoBase';
import catalogoPisteiroConfigService from '../../services/catalogoPisteiroConfigService';
import CatalogoLeilaoButton from '../../components/catalogos/CatalogoLeilaoButton';

const formatarPercentual = (pct) => Number.isInteger(pct) ? pct : parseFloat(pct.toFixed(1));

const CatalogoPisteiro = () => {
  const { leilaoId } = useParams();
  const navigate = useNavigate();
  const { organizadorAtivo } = useOrganizador();
  const [leilao, setLeilao] = useState(null);
  const [leiloes, setLeiloes] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modoPeso, setModoPeso] = useState('peso_arroba'); // 'peso_arroba' | 'peso'
  const [config, setConfig] = useState({
    colunas_ativas: ['lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'peso', 'curral', 'valor_inicial'],
    mostrar_totais: true,
    mostrar_rodape: true,
    lotes_por_pagina: 33
  });

  const getColunasLarguras = (temObservacoes) => {
    const pesoLabel = modoPeso === 'peso_arroba' ? 'Peso/@' : 'Peso(kg)';
    if (temObservacoes) {
      return {
        ordem: { id: 'ordem', label: 'O.D', width: 'w-7' },
        lote: { id: 'lote', label: 'Lote', width: 'w-9' },
        vendedor: { id: 'vendedor', label: 'Vendedor', width: 'w-28' },
        quantidade: { id: 'quantidade', label: 'Qtde', width: 'w-7' },
        tipo: { id: 'tipo', label: 'Tipo', width: 'w-14' },
        raca: { id: 'raca', label: 'Raça', width: 'w-14' },
        sexo: { id: 'sexo', label: 'Sexo', width: 'w-7' },
        idade: { id: 'idade', label: 'Idade', width: 'w-9' },
        peso: { id: 'peso', label: pesoLabel, width: 'w-14' },
        curral: { id: 'curral', label: 'Curral', width: 'w-9' },
        observacoes: { id: 'observacoes', label: 'Observações', width: 'flex-1 min-w-[200px]' },
        valor_inicial: { id: 'valor_inicial', label: 'Valor Mínimo', width: 'w-18' },
        pelagem: { id: 'pelagem', label: 'Pelagem', width: 'w-14' },
        registro: { id: 'registro', label: 'Registro', width: 'w-14' },
        percentual_venda: { id: 'percentual_venda', label: '% Venda', width: 'w-9' },
        localizacao: { id: 'localizacao', label: 'Localização', width: 'w-16' },
        animal: { id: 'animal', label: 'Animal', width: 'w-40' },
        pai: { id: 'pai', label: 'Pai', width: 'w-14' },
        mae: { id: 'mae', label: 'Mãe', width: 'w-14' },
        data_nascimento: { id: 'data_nascimento', label: 'Nasc.', width: 'w-14' },
      };
    } else {
      return {
        ordem: { id: 'ordem', label: 'O.D', width: 'w-10' },
        lote: { id: 'lote', label: 'Lote', width: 'w-12' },
        vendedor: { id: 'vendedor', label: 'Vendedor', width: 'w-40' },
        quantidade: { id: 'quantidade', label: 'Qtde', width: 'w-10' },
        tipo: { id: 'tipo', label: 'Tipo', width: 'w-20' },
        raca: { id: 'raca', label: 'Raça', width: 'w-20' },
        sexo: { id: 'sexo', label: 'Sexo', width: 'w-10' },
        idade: { id: 'idade', label: 'Idade', width: 'w-12' },
        peso: { id: 'peso', label: pesoLabel, width: 'w-14' },
        curral: { id: 'curral', label: 'Curral', width: 'w-12' },
        observacoes: { id: 'observacoes', label: 'Observações', width: 'w-32' },
        valor_inicial: { id: 'valor_inicial', label: 'Valor Mínimo', width: 'w-24' },
        pelagem: { id: 'pelagem', label: 'Pelagem', width: 'w-20' },
        registro: { id: 'registro', label: 'Registro', width: 'w-20' },
        percentual_venda: { id: 'percentual_venda', label: '% Venda', width: 'w-12' },
        localizacao: { id: 'localizacao', label: 'Localização', width: 'w-24' },
        animal: { id: 'animal', label: 'Animal', width: 'w-52' },
        pai: { id: 'pai', label: 'Pai', width: 'w-20' },
        mae: { id: 'mae', label: 'Mãe', width: 'w-20' },
        data_nascimento: { id: 'data_nascimento', label: 'Nasc.', width: 'w-20' },
      };
    }
  };

  useEffect(() => {
    if (organizadorAtivo?.id) {
      carregarLeiloes();
    }
  }, [organizadorAtivo]);

  useEffect(() => {
    if (leilaoId && organizadorAtivo?.id) {
      carregarDados();
    }
  }, [leilaoId, organizadorAtivo]);

  const carregarLeiloes = async () => {
    try {
      const data = await leiloesService.getByOrganizador(organizadorAtivo.id);
      setLeiloes(data || []);
    } catch (error) {
      console.error('Erro ao carregar leilões:', error);
    }
  };

  const handleLeilaoChange = (novoLeilaoId) => {
    if (novoLeilaoId !== leilaoId) {
      navigate(`/catalogos/pisteiro/${novoLeilaoId}`);
    }
  };

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [leilaoData, ordemData, lotesCompletos, configData] = await Promise.all([
        leiloesService.getById(organizadorAtivo.id, leilaoId),
        ordemEntradaService.listarOrdem(leilaoId).catch(() => []),
        lotesService.listarLotesPorLeilao(organizadorAtivo.id, leilaoId),
        catalogoPisteiroConfigService.getConfig(organizadorAtivo.id).catch(() => null)
      ]);
      
      let lotesComDados;
      
      if (ordemData && ordemData.length > 0) {
        const idsNaOrdem = new Set(ordemData.map(o => o.lote_id));
        const lotesOrdenados = ordemData
          .map(itemOrdem => ({
            ...lotesCompletos.find(l => l.id === itemOrdem.lote_id),
            ...itemOrdem,
          }))
          .sort((a, b) => a.posicao - b.posicao);

        const lotesFora = lotesCompletos
          .filter(l => !idsNaOrdem.has(l.id))
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0));

        const offset = lotesOrdenados.length;
        lotesComDados = [
          ...lotesOrdenados,
          ...lotesFora.map((lote, i) => ({ ...lote, posicao: offset + i + 1, lote_id: lote.id, lote_numero: lote.numero_lote })),
        ];
      } else {
        lotesComDados = lotesCompletos
          .sort((a, b) => (parseInt(a.numero_lote) || 0) - (parseInt(b.numero_lote) || 0))
          .map((lote, index) => ({ ...lote, posicao: index + 1 }));
      }
      
      setLeilao(leilaoData);
      setLotes(lotesComDados);
      if (configData) {
        setConfig(configData);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Idade calculada a partir da data de nascimento do animal (fluxo cavalo —
  // LoteAnimal não tem cadastro de Idade como o lote de gado). Mesma lógica
  // de reports/animal_spec_utils.py:idade_label().
  const idadeLabel = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const nasc = new Date(dataNascimento);
    const hoje = new Date();
    let meses = (hoje.getFullYear() - nasc.getFullYear()) * 12 + (hoje.getMonth() - nasc.getMonth());
    if (hoje.getDate() < nasc.getDate()) meses -= 1;
    meses = Math.max(0, meses);
    if (meses < 24) return `${meses} MES${meses !== 1 ? 'ES' : ''}`;
    const anos = Math.floor(meses / 12);
    return `${anos} ANO${anos !== 1 ? 'S' : ''}`;
  };

  const getObsTexto = (item) => {
    let obs = item.observacoes || '';
    const criasF = item.qtd_crias_femeas || 0;
    const criasM = item.qtd_crias_machos || 0;
    if (criasF > 0 || criasM > 0) {
      const partes = [];
      if (criasF > 0) partes.push(`F:${criasF}`);
      if (criasM > 0) partes.push(`M:${criasM}`);
      const criasTxt = `Crias: ${partes.join(' ')}`;
      obs = obs ? `${criasTxt} | ${obs}` : criasTxt;
    }
    return obs || '-';
  };

  const renderCelula = (item, colunaId) => {
    switch (colunaId) {
      case 'ordem':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center font-bold text-xs">{item.posicao}</td>;
      case 'lote':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 font-medium text-xs">{item.numero_lote}</td>;
      case 'vendedor':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 truncate text-xs">{(item.vendedor?.nome || '-').length > 25 ? (item.vendedor?.nome || '-').substring(0, 25) + '...' : (item.vendedor?.nome || '-')}</td>;
      case 'quantidade':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item._animal ? '1' : (item.qtd || '-')}</td>;
      case 'tipo': {
        const tipoObj = item._animal ? item._animal.tipo_animal : item.tipo_animal;
        let nome = tipoObj?.nome || '-';
        if (nome.toUpperCase().includes('VACA COM CRIA')) {
          nome = nome.toUpperCase().replace('VACA COM CRIA', 'VACA C/ CRIA');
        }
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{nome}</td>;
      }
      case 'raca': {
        const racaObj = item._animal ? item._animal.raca : item.raca;
        if (!racaObj?.nome) return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">-</td>;
        const nomeRaca = racaObj.nome.toUpperCase();
        if (!item._animal) {
          return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{nomeRaca.length > 12 ? nomeRaca.substring(0, 12) : nomeRaca}</td>;
        }
        const partesRaca = nomeRaca.split(' ');
        const raca = (partesRaca.length > 1 && nomeRaca.length > 14) ? `${partesRaca[0][0]}. ${partesRaca.slice(1).join(' ')}` : nomeRaca;
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{raca}</td>;
      }
      case 'sexo': {
        const sexo = item._animal ? (SEXO_LABEL[item._animal.sexo] || item._animal.sexo) : item.sexo;
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{sexo === 'Macho' ? 'M' : sexo === 'Fêmea' ? 'F' : sexo || '-'}</td>;
      }
      case 'idade':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item._animal ? idadeLabel(item._animal.data_nascimento) : (item.idade?.nome || '-')}</td>;
      case 'peso':
        return (
          <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">
            {(() => {
              const peso = item._animal ? item._animal.peso : (item.peso_medio || item.peso);
              if (!peso) return '-';
              const pesoNum = parseFloat(peso);
              if (modoPeso === 'peso_arroba' && pesoNum > 100) {
                const arrobas = (pesoNum / 30).toFixed(1);
                return `${parseFloat(peso).toFixed(0)}/${arrobas}@`;
              }
              return parseFloat(peso).toFixed(1);
            })()}
          </td>
        );
      case 'curral':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item.curral || '-'}</td>;
      case 'observacoes':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs observacoes-col" style={{fontSize: '10px', whiteSpace: 'normal', wordBreak: 'break-word'}}>{getObsTexto(item)}</td>;
      case 'valor_inicial':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item.valor_minimo_unidade ? `R$ ${parseFloat(item.valor_minimo_unidade).toFixed(2)}` : '-'}</td>;
      case 'pelagem':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{item._animal?.pelagem?.toUpperCase() || '-'}</td>;
      case 'registro':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{item._animal?.registro?.trim() ? <span className="font-bold">{item._animal.registro.toUpperCase()}</span> : '-'}</td>;
      case 'percentual_venda':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item._animal ? `${formatarPercentual(parseFloat(item._animal.percentual_venda ?? 100))}%` : '-'}</td>;
      case 'localizacao':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{item._animal?.localizacao?.toUpperCase() || '-'}</td>;
      case 'pai':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{item._animal?.pai?.trim() ? item._animal.pai.toUpperCase() : '-'}</td>;
      case 'mae':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">{item._animal?.mae?.trim() ? item._animal.mae.toUpperCase() : '-'}</td>;
      case 'data_nascimento':
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-center text-xs">{item._animal?.data_nascimento ? new Date(item._animal.data_nascimento).toLocaleDateString('pt-BR') : '-'}</td>;
      case 'animal': {
        if (!item._animal) return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">-</td>;
        const nome = (item._animal.nome || '-').toUpperCase();
        const pct = parseFloat(item._animal.percentual_venda ?? 100);
        const linha1 = Math.abs(pct - 100) > 0.001 ? `${formatarPercentual(pct)}% ${nome}` : nome;
        const registroComoColuna = colunasAtivas.includes('registro');
        const registro = !registroComoColuna ? (item._animal.registro || '').trim() : '';
        return (
          <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">
            <span className="font-bold">{linha1}</span>
            {registro && (<><br /><span className="font-bold text-gray-500" style={{ fontSize: '0.65rem' }}>{registro}</span></>)}
          </td>
        );
      }
      default:
        return <td key={colunaId} className="border border-gray-300 px-1 py-1 text-xs">-</td>;
    }
  };

  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo';
  const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' };
  const ehCavalo = (lote) => !lote.tipo_animal;
  const temDestaque = (item) => item._animal && Math.abs(parseFloat(item._animal.percentual_venda ?? 100) - 100) > 0.001;
  const linhasExpandidas = lotes.flatMap((lote) => {
    const animais = ehCavalo(lote) ? (lote.animais || []) : [];
    if (animais.length > 0) {
      return animais.map((animal) => ({ ...lote, _animal: animal }));
    }
    return [{ ...lote, _animal: null }];
  });
  const corPorLote = {};
  let _proxPar = true;
  linhasExpandidas.forEach((item) => {
    if (!(item.id in corPorLote)) {
      corPorLote[item.id] = _proxPar;
      _proxPar = !_proxPar;
    }
  });

  // Observação + localização, mesma linha. Gado: observação do lote (de
  // sempre). Cavalo: observação do PRÓPRIO ANIMAL (informacoes) + sua
  // localização.
  const obsMaisLocalizacao = (item) => {
    if (!item._animal) {
      const obs = getObsTexto(item);
      return obs !== '-' ? obs : '';
    }
    const obs = (item._animal.informacoes || '').trim();
    const local = (item._animal.localizacao || '').trim();
    const partes = [];
    if (obs) partes.push(`OBS. ${obs}`);
    if (local) partes.push(`LOCAL ${local.toUpperCase()}`);
    return partes.join('  ·  ');
  };

  const ordemColunasGado = ['ordem', 'lote', 'vendedor', 'quantidade', 'tipo', 'raca', 'sexo', 'idade', 'peso', 'curral', 'observacoes', 'valor_inicial', 'pelagem', 'registro', 'percentual_venda', 'localizacao'];
  const ordemColunasCavalo = ['ordem', 'lote', 'animal', 'registro', 'raca', 'data_nascimento', 'pai', 'mae', 'idade', 'sexo', 'tipo', 'peso', 'curral', 'vendedor', 'observacoes', 'valor_inicial', 'pelagem', 'percentual_venda', 'localizacao'];
  const ordemColunas = isFluxoCavalo ? ordemColunasCavalo : ordemColunasGado;
  const colunasAtivas = ordemColunas.filter(col => config.colunas_ativas.includes(col));
  const temObservacoes = colunasAtivas.includes('observacoes');
  const obsSempreAbaixo = temObservacoes && !!config.observacao_sempre_abaixo;
  const colunasTabela = (obsSempreAbaixo || isFluxoCavalo)
    ? colunasAtivas.filter(col => col !== 'observacoes')
    : colunasAtivas;
  const colunasMapeamento = getColunasLarguras(temObservacoes && !obsSempreAbaixo);
  const lotesPrimeiraPagina = 33;
  const lotesProximasPaginas = 38;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="print-area">
      <CatalogoBase
        titulo="CATÁLOGO PISTEIRO"
        subtitulo={leilao ? (
          <div>
            <div>{leilao.nome}</div>
            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-6 text-gray-600 text-sm sm:text-base mt-2">
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                {new Date(leilao.data_leilao).toLocaleDateString('pt-BR')}
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} />
                {leilao.cidade}, {leilao.uf}
              </div>
            </div>
          </div>
        ) : null}
        onVoltar={() => window.history.back()}
        logoOrganizador={organizadorAtivo?.logo_url}
        bannerLeilao={leilao?.banner_url ? (leilao.banner_url.startsWith('http') ? leilao.banner_url : `${(import.meta.env.VITE_API_URL || 'http://localhost:8001/').replace(/\/$/, '')}${leilao.banner_url}`) : null}
        landscape={false}
        showHeaderInfo={false}
        leiloes={leiloes}
        selectedLeilao={leilaoId}
        onLeilaoChange={handleLeilaoChange}
        extraButtons={
          leilaoId && organizadorAtivo?.id ? (
            <CatalogoLeilaoButton
              leilaoId={leilaoId}
              organizadorId={organizadorAtivo.id}
              leilaoNome={leilao?.nome || ''}
              label="Imprimir"
              endpoint="catalogo-pisteiro"
              permitirPaisagem
            />
          ) : null
        }
      >
        {/* No celular a tabela tem colunas demais pra ficar legível — orienta a usar Imprimir */}
        <div className="sm:hidden text-center py-10 px-4 text-gray-500">
          <Printer size={32} className="mx-auto mb-3 text-gray-400" />
          <p className="font-medium text-gray-700">Visualização em tabela não disponível no celular</p>
          <p className="text-sm mt-1">Use o botão <strong>Imprimir</strong> no topo da página para gerar o PDF do catálogo.</p>
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                {colunasTabela.map(colunaId => {
                  const coluna = colunasMapeamento[colunaId];
                  if (!coluna) return null;
                  return (
                    <th key={colunaId} className={`border border-gray-300 px-1 py-1.5 text-center font-semibold text-xs ${coluna.width}`}>
                      {coluna.label}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {linhasExpandidas.length > 0 ? (
                linhasExpandidas.map((item, index) => {
                  const destaque = temDestaque(item);
                  const bg = destaque ? 'bg-amber-100' : (corPorLote[item.id] ? 'bg-white' : 'bg-gray-50');
                  const primeiraLinhaDoLote = index === 0 || linhasExpandidas[index - 1].id !== item.id;
                  const textoAbaixo = item._animal ? obsMaisLocalizacao(item) : (obsSempreAbaixo ? getObsTexto(item) : null);
                  const mostrarAbaixo = item._animal ? !!textoAbaixo : (primeiraLinhaDoLote && obsSempreAbaixo && textoAbaixo !== '-');
                  return (
                  <React.Fragment key={`${item.id}-${item._animal?.id ?? 0}`}>
                    <tr className={`${bg} ${destaque ? 'font-bold' : ''}`}>
                      {colunasTabela.map(colunaId => renderCelula(item, colunaId))}
                    </tr>
                    {mostrarAbaixo && (
                      <tr className={corPorLote[item.id] ? 'bg-white' : 'bg-gray-50'}>
                        <td colSpan={colunasTabela.length} className="border border-gray-300 px-1 pb-1 pt-0 text-sm text-gray-700" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {textoAbaixo}
                        </td>
                      </tr>
                    )}
                    {config.incluir_linha_compradores && (index + 1 >= linhasExpandidas.length || linhasExpandidas[index + 1].id !== item.id) && (
                      <tr className={corPorLote[item.id] ? 'bg-white' : 'bg-gray-50'}>
                        <td colSpan={colunasTabela.length} className="border border-gray-300 px-1 pt-3 pb-1 text-sm font-semibold text-gray-700">
                          Comprador:
                        </td>
                      </tr>
                    )}
                    {(() => {
                      if (index + 1 === lotesPrimeiraPagina) {
                        return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                      } else if (index + 1 > lotesPrimeiraPagina && (index + 1 - lotesPrimeiraPagina) % lotesProximasPaginas === 0) {
                        return <tr className="page-break-after"><td colSpan={colunasTabela.length} style={{height: '0px', padding: '0px', border: 'none'}}></td></tr>;
                      }
                      return null;
                    })()}
                  </React.Fragment>
                );})
              ) : (
                <tr>
                  <td colSpan={colunasAtivas.length} className="border border-gray-300 px-3 py-8 text-center text-gray-500">
                    Nenhum lote encontrado na ordem de entrada
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {config.mostrar_totais && (
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-item">
                <div className="total-number text-gray-900">{lotes.length}</div>
                <div className="total-label">Total de Lotes</div>
              </div>
              <div className="total-item">
                <div className="total-number text-blue-600">
                  {linhasExpandidas
                    .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Macho')
                    .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Machos</div>
              </div>
              <div className="total-item">
                <div className="total-number text-pink-600">
                  {linhasExpandidas
                    .filter(l => (l._animal ? SEXO_LABEL[l._animal.sexo] : l.sexo) === 'Fêmea')
                    .reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Fêmeas</div>
              </div>
              <div className="total-item">
                <div className="total-number text-purple-600">
                  {linhasExpandidas.reduce((sum, l) => sum + (l._animal ? 1 : (l.qtd || 0)), 0)}
                </div>
                <div className="total-label">Total de Animais</div>
              </div>
            </div>
          </div>
        )}
        
        {config.mostrar_rodape && (
          <div className="footer-final">
            www.rematei.com.br | Gerado em {new Date().toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
          </div>
        )}
      </CatalogoBase>
    </div>
  );
};

export default CatalogoPisteiro;